/**
 * reCAPTCHA v3 for unauthenticated Medplum auth endpoints (e.g. /auth/resetpassword).
 * Site key must match the Medplum Project reCAPTCHA configuration on hosted Medplum.
 */
const RECAPTCHA_SCRIPT_ID = 'unify-recaptcha-v3';

type Grecaptcha = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

function getRecaptchaSiteKey(): string {
  return (import.meta.env.VITE_RECAPTCHA_SITE_KEY || '').trim();
}

function getGrecaptcha(): Grecaptcha | undefined {
  return (window as typeof window & { grecaptcha?: Grecaptcha }).grecaptcha;
}

/** Wait until grecaptcha.ready is callable (script may finish downloading before the API is defined). */
function waitForGrecaptcha(timeoutMs = 10_000): Promise<Grecaptcha> {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;

    const attempt = (): void => {
      const grecaptcha = getGrecaptcha();
      if (grecaptcha?.ready) {
        grecaptcha.ready(() => {
          resolve(grecaptcha);
        });
        return;
      }
      if (Date.now() >= deadline) {
        reject(new Error('reCAPTCHA is not available'));
        return;
      }
      setTimeout(attempt, 50);
    };

    attempt();
  });
}

function loadRecaptchaScript(siteKey: string): Promise<void> {
  const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    if (existing.dataset.loaded === 'true') {
      return waitForGrecaptcha().then(() => undefined);
    }
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => {
        waitForGrecaptcha().then(() => {
          existing.dataset.loaded = 'true';
          resolve();
        }, reject);
      }, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => {
      waitForGrecaptcha()
        .then(() => {
          script.dataset.loaded = 'true';
          resolve();
        })
        .catch(reject);
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });
}

/** Returns reCAPTCHA fields for Medplum auth requests when VITE_RECAPTCHA_SITE_KEY is set. */
export async function getMedplumRecaptchaFields(
  action = 'reset_password',
): Promise<{ recaptchaSiteKey: string; recaptchaToken: string } | undefined> {
  const siteKey = getRecaptchaSiteKey();
  if (!siteKey) return undefined;

  await loadRecaptchaScript(siteKey);
  const grecaptcha = await waitForGrecaptcha();
  const recaptchaToken = await grecaptcha.execute(siteKey, { action });
  return { recaptchaSiteKey: siteKey, recaptchaToken };
}

export function isHostedMedplumBaseUrl(baseUrl: string): boolean {
  return /api\.medplum\.com/i.test(baseUrl);
}
